import React from 'react';
import { MDBIcon, MDBBtn } from 'mdbreact';
import { TreeSelect, DatePicker, message, Spin, Mentions } from 'antd';
import moment from 'moment';
import axios from 'axios';
import { BASE_END_POINT } from '../../urls';
import './taskInfo.css';

const { TreeNode } = TreeSelect;
const { Option: MentionOption } = Mentions;

// ─── Helpers ─────────────────────────────────────────────────────────────────

const TAG_COLORS = [
  { bg: '#E3F2FD', text: '#1E88E5' },
  { bg: '#E8F5E9', text: '#43A047' },
  { bg: '#F3E5F5', text: '#8E24AA' },
  { bg: '#FFEBEE', text: '#E53935' },
  { bg: '#FFF3E0', text: '#F57C00' },
  { bg: '#E0F2F1', text: '#00897B' },
  { bg: '#FCE4EC', text: '#E91E63' },
  { bg: '#EDE7F6', text: '#673AB7' },
];
const getTagColor = (tag) => {
  let hash = 0;
  for (let i = 0; i < tag.length; i++) hash = tag.charCodeAt(i) + ((hash << 5) - hash);
  return TAG_COLORS[Math.abs(hash) % TAG_COLORS.length];
};

const STATUS_MAP = {
  'PENDING': { label: 'بالانتظار', bg: '#E3F2FD', color: '#1E88E5' },
  'IN-PROGRESS': { label: 'جاري التنفيذ', bg: '#FFF3E0', color: '#F57C00' },
  'OVERDUE': { label: 'متأخرة', bg: '#FFEBEE', color: '#E53935' },
  'FINISHED': { label: 'منتهية', bg: '#E8F5E9', color: '#43A047' },
};

const getFileIcon = (url = '') => {
  const ext = url.split('.').pop().toLowerCase();
  if (['xlsx', 'xls', 'csv'].includes(ext)) return 'file-excel';
  if (['docx', 'doc'].includes(ext)) return 'file-word';
  if (ext === 'pdf') return 'file-pdf';
  if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext)) return 'file-image';
  return 'file-alt';
};
const getFileName = (url = '') => {
  try { return decodeURIComponent(url.split('/').pop().split('?')[0]); } catch { return url; }
};

// ─── Emoji Data ──────────────────────────────────────────────────────────────

const EMOJI_CATEGORIES = [
  { name: 'وجوه', emojis: ['😀','😂','😅','🤣','😊','😍','🥰','😘','😜','🤪','😎','🤩','🥳','😇','🤗','🤔','🤭','😏','😌','😴','🤤','😋','🤓','😤','😡','🥺','😢','😭','😱','🤯','😳','🥴','🤢','🤮','🤧','😷','🤒'] },
  { name: 'إيماءات', emojis: ['👍','👎','👌','✌️','🤞','🤟','🤘','👏','🙌','🤝','🙏','💪','👋','🤙','✋','👊','👆','👇','👈','👉','☝️'] },
  { name: 'قلوب', emojis: ['❤️','🧡','💛','💚','💙','💜','🤎','🖤','🤍','💔','❣️','💕','💞','💓','💗','💖','💝','💘'] },
  { name: 'رموز', emojis: ['⭐','🌟','✨','💫','🔥','💯','✅','❌','⚠️','🚀','💡','🎯','🏆','🎉','🎊','📌','📎','🔗','💬','💭','🕐','📅'] },
  { name: 'طبيعة', emojis: ['🌞','🌙','⭐','🌈','☁️','⛅','🌧️','⚡','❄️','🌸','🌺','🌻','🌹','🍀','🌿','🌴'] },
];

// ─── Component ────────────────────────────────────────────────────────────────

class TaskInfo extends React.Component {
  state = {
    // localTask: source of truth for display — updated optimistically after each save
    localTask: null,
    hasEdited: false,      // if true → onClose will trigger board refresh

    // Comments
    comments: [],
    loadingComments: false,
    commentText: '',
    editingCommentId: null,
    editingCommentText: '',

    // Files
    uploadingFile: false,
    taskFiles: [],

    // Tags input
    newTagName: '',

    // Inline edit
    editingField: null,    // 'title' | 'description' | null
    editFieldValue: '',
    saving: false,

    // Emoji picker
    showEmojiPicker: false,
    emojiCategory: 0,
  };

  fileInputRef = React.createRef();
  mentionsBackdropRef = React.createRef();
  editMentionsBackdropRef = React.createRef();
  emojiPickerRef = React.createRef();

  handleMentionsScroll = (e) => {
    if (this.mentionsBackdropRef.current) {
      this.mentionsBackdropRef.current.scrollTop = e.target.scrollTop;
    }
  };

  handleEditMentionsScroll = (e) => {
    if (this.editMentionsBackdropRef.current) {
      this.editMentionsBackdropRef.current.scrollTop = e.target.scrollTop;
    }
  };

  componentDidMount() {
    const { selectedTask } = this.props;
    const taskId = selectedTask?.id || selectedTask?._id;
    const taskWithId = selectedTask ? { ...selectedTask, id: taskId, _id: taskId } : null;

    this.loadComments();
    this.setState({
      localTask: taskWithId,
      taskFiles: selectedTask?.files || [],
      hasEdited: false,
    });

    document.addEventListener('mousedown', this.handleClickOutsideEmoji);
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutsideEmoji);
  }

  handleClickOutsideEmoji = (e) => {
    if (this.emojiPickerRef.current && !this.emojiPickerRef.current.contains(e.target)) {
      this.setState({ showEmojiPicker: false });
    }
  };

  componentDidUpdate(prevProps) {
    const { selectedTask } = this.props;
    const prevId = prevProps.selectedTask?.id || prevProps.selectedTask?._id;
    const currentId = selectedTask?.id || selectedTask?._id;
    const taskWithId = selectedTask ? { ...selectedTask, id: currentId, _id: currentId } : null;

    if (prevId !== currentId) {
      // New task opened
      this.loadComments();
      this.setState({
        localTask: taskWithId,
        taskFiles: selectedTask?.files || [],
        editingField: null,
        hasEdited: false,
      });
    } else if (prevProps.selectedTask !== selectedTask && selectedTask) {
      // Task reference updated from parent
      this.setState({
        localTask: taskWithId,
        taskFiles: selectedTask.files || [],
      });
    }
  }

  authHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${this.props.currentUser.token}`,
    'Accept-Language': this.props.isRTL ? 'ar' : 'en',
  });

  // ─── Handle Close: refresh board only if something was edited ────────────

  handleClose = () => {
    const { onClose, onRefreshTask } = this.props;
    if (this.state.hasEdited) {
      onRefreshTask && onRefreshTask();
    }
    onClose();
  };

  // ─── Core: Update Task ────────────────────────────────────────────────────
  // Sends PUT request and updates localTask on success — NO board refresh here.

  updateTask = (overrides = {}) => {
    const { employees, projects, isRTL } = this.props;
    const task = this.state.localTask;
    if (!task) return;

    // Always send title + description
    const title = overrides.title !== undefined ? overrides.title : task.title;
    const description = overrides.description !== undefined ? overrides.description : (task.description || '');

    // Extract IDs (whether stored as object or primitive)
    const assignedToId = overrides.assignedTo !== undefined
      ? overrides.assignedTo
      : (typeof task.assignedTo === 'object' ? (task.assignedTo?.id || task.assignedTo?._id) : task.assignedTo);

    const projectId = overrides.project !== undefined
      ? overrides.project
      : (typeof task.project === 'object' ? (task.project?.id || task.project?._id) : task.project);

    const deadline = overrides.deadline !== undefined
      ? overrides.deadline
      : task.deadline;

    const tags = overrides.tags !== undefined
      ? overrides.tags
      : (task.tags || []);

    const status = overrides.status !== undefined
      ? overrides.status
      : task.status;

    if (!title?.trim()) { message.warning('العنوان مطلوب'); return; }

    const data = new FormData();
    data.append('title', title);
    data.append('description', description);
    if (assignedToId) data.append('assignedTo', assignedToId);
    if (projectId) data.append('project', projectId);
    if (deadline) data.append('deadline', moment(deadline).toISOString());
    if (status) data.append('status', status);
    tags.forEach(t => {
      const str = typeof t === 'string' ? t : (t.text || '');
      if (str) data.append('tags[]', str);
    });

    const taskId = task.id || task._id;
    if (!taskId) {
      message.error('معرف المهمة غير صالح');
      return;
    }

    this.setState({ saving: true });

    axios.put(`${BASE_END_POINT}tasks/${taskId}`, data, {
      headers: {
        'Content-Type': 'multipart/form-data',
        'Authorization': `Bearer ${this.props.currentUser.token}`,
        'Accept-Language': this.props.isRTL ? 'ar' : 'en',
      },
    })
      .then(() => {
        const emp = (employees || []).find(e => String(e.id || e._id) === String(assignedToId));
        const proj = (projects || []).find(p => String(p.id || p._id) === String(projectId));

        const updated = {
          ...task,
          title,
          description,
          deadline,
          tags,
          status,
          assignedTo: emp
            ? { fullname: emp.fullname, img: emp.img, type: emp.type, id: emp.id || emp._id, _id: emp.id || emp._id }
            : (typeof task.assignedTo === 'object' ? task.assignedTo : null),
          project: proj
            ? {
              name: isRTL ? proj.name_ar : proj.name_en,
              name_ar: proj.name_ar,
              name_en: proj.name_en,
              id: proj.id || proj._id,
              _id: proj.id || proj._id,
            }
            : (typeof task.project === 'object' ? task.project : null),
          id: taskId,
          _id: taskId,
        };

        this.setState({ saving: false, hasEdited: true, localTask: updated }, () => {
          if (overrides.status && this.props.onRefreshTask) {
            this.props.onRefreshTask();
          }
        });
      })
      .catch(err => {
        this.setState({ saving: false });
        console.log('updateTask error', err.response);
        message.error(err.response?.data?.errors?.[0]?.msg || 'حدث خطأ أثناء الحفظ', 2);
      });
  };

  handleToggleFinish = () => {
    const task = this.state.localTask;
    if (!task) return;
    const isFinished = task.status === 'FINISHED';
    const newStatus = isFinished ? 'PENDING' : 'FINISHED';
    const msgText = newStatus === 'FINISHED' ? 'تم إنهاء المهمة' : 'تم إرجاع المهمة إلى بالانتظار';

    this.updateTask({ status: newStatus });
    message.success(msgText, 2.5);
  };

  // ─── Inline Edit: Title / Description ─────────────────────────────────────

  startEditing = (field) => {
    const task = this.state.localTask;
    this.setState({
      editingField: field,
      editFieldValue: field === 'title' ? (task?.title || '') : (task?.description || ''),
    });
  };

  cancelEditing = () => this.setState({ editingField: null, editFieldValue: '' });

  saveInlineField = () => {
    const { editingField, editFieldValue } = this.state;
    this.updateTask({ [editingField]: editFieldValue });
    this.setState({ editingField: null, editFieldValue: '' });
  };

  // ─── Comments API ──────────────────────────────────────────────────────────

  loadComments = () => {
    const { selectedTask } = this.props;
    const taskId = selectedTask?.id || selectedTask?._id;
    if (!taskId) return;
    this.setState({ loadingComments: true });
    axios.get(`${BASE_END_POINT}tasks/${taskId}/comments`, { headers: this.authHeaders() })
      .then(res => this.setState({ comments: res.data.data || [], loadingComments: false }))
      .catch(() => this.setState({ loadingComments: false }));
  };

  handleAddComment = () => {
    const { commentText } = this.state;
    const { selectedTask } = this.props;
    const taskId = selectedTask?.id || selectedTask?._id;
    if (!commentText.trim() || !taskId) return;
    const l = message.loading('جاري الإرسال...', 0);
    axios.post(`${BASE_END_POINT}tasks/${taskId}/comments`, { comment: commentText }, { headers: this.authHeaders() })
      .then(() => { l(); this.setState({ commentText: '' }); this.loadComments(); })
      .catch(err => { l(); message.error(err.response?.data?.errors?.[0]?.msg || 'حدث خطأ', 2); });
  };

  handleDeleteComment = (commentId) => {
    const { selectedTask } = this.props;
    const taskId = selectedTask?.id || selectedTask?._id;
    if (!taskId) return;
    const l = message.loading('جاري الحذف...', 0);
    axios.delete(`${BASE_END_POINT}tasks/${taskId}/comments/${commentId}`, { headers: this.authHeaders() })
      .then(() => { l(); this.setState(prev => ({ comments: prev.comments.filter(c => c.id !== commentId) })); })
      .catch(() => { l(); message.error('حدث خطأ', 2); });
  };

  handleUpdateComment = (commentId) => {
    const { editingCommentText } = this.state;
    const { selectedTask } = this.props;
    const taskId = selectedTask?.id || selectedTask?._id;
    if (!editingCommentText.trim() || !taskId) return;
    const l = message.loading('جاري التعديل...', 0);
    axios.put(`${BASE_END_POINT}tasks/${taskId}/comments/${commentId}`, { comment: editingCommentText }, { headers: this.authHeaders() })
      .then(() => {
        l();
        this.setState(prev => ({
          comments: prev.comments.map(c => c.id === commentId ? { ...c, comment: editingCommentText } : c),
          editingCommentId: null,
          editingCommentText: '',
        }));
      })
      .catch(() => { l(); message.error('حدث خطأ', 2); });
  };

  // ─── Files API ─────────────────────────────────────────────────────────────

  handleAddFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const inputEl = e.target;
    const { selectedTask, onRefreshTask } = this.props;
    const taskId = selectedTask?.id || selectedTask?._id;
    if (!taskId) return;
    this.setState({ uploadingFile: true });
    const data = new FormData();
    data.append('file', file);
    axios.post(`${BASE_END_POINT}tasks/${taskId}/addFile`, data, {
      headers: {
        'Content-Type': 'multipart/form-data',
        'Authorization': `Bearer ${this.props.currentUser.token}`,
        'Accept-Language': this.props.isRTL ? 'ar' : 'en',
      },
    })
      .then(() => {
        this.setState({ uploadingFile: false });
        message.success('تم رفع الملف بنجاح', 2);
        try { inputEl.value = ''; } catch (_) { }
        // Refresh board to get updated files list
        onRefreshTask && onRefreshTask();
      })
      .catch(err => {
        this.setState({ uploadingFile: false });
        message.error(err.response?.data?.errors?.[0]?.msg || 'حدث خطأ أثناء رفع الملف', 2);
      });
  };

  handleRemoveFile = (fileUrl) => {
    const { selectedTask } = this.props;
    const taskId = selectedTask?.id || selectedTask?._id;
    if (!taskId) return;
    const l = message.loading('جاري الحذف...', 0);
    axios.delete(`${BASE_END_POINT}tasks/${taskId}/removeFile`, {
      headers: this.authHeaders(),
      data: { fileUrl },
    })
      .then(() => {
        l();
        message.success('تم حذف الملف', 2);
        this.setState(prev => ({ taskFiles: prev.taskFiles.filter(f => f !== fileUrl) }));
      })
      .catch(() => { l(); message.error('حدث خطأ', 2); });
  };

  // ─── Delete Task ───────────────────────────────────────────────────────────

  handleDeleteTask = () => {
    const { selectedTask, onRefreshTask } = this.props;
    const taskId = selectedTask?.id || selectedTask?._id;
    if (!taskId) return;
    const l = message.loading('جاري حذف المهمة...', 0);
    axios.delete(`${BASE_END_POINT}tasks/${taskId}`, { headers: this.authHeaders() })
      .then(() => {
        l();
        message.success('تم حذف المهمة', 2.5);
        this.props.onClose();
        onRefreshTask && onRefreshTask();
      })
      .catch(() => { l(); message.error('حدث خطأ', 2); });
  };

  renderCommentText = (text) => {
    if (!text) return null;
    const { employees } = this.props;

    const names = (employees || [])
      .map(e => e.fullname || e.name)
      .filter(Boolean)
      .sort((a, b) => b.length - a.length);

    const escapedNames = names.map(n => n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
    let regexString = '@(?:' + (escapedNames.length > 0 ? escapedNames.join('|') + '|' : '') + '[\\u0600-\\u06FF\\w]+)';
    const pattern = new RegExp(`(${regexString})`, 'g');

    const parts = text.split(pattern);

    return parts.map((part, idx) => {
      if (part && part.startsWith('@')) {
        return (
          <span key={idx} className="mention-badge">
            {part}
          </span>
        );
      }
      return part;
    });
  };

  // ─── Render ────────────────────────────────────────────────────────────────

  render() {
    const {
      activeTab, employees, projects, isRTL, onSetActiveTab,
    } = this.props;

    const {
      localTask,
      comments, loadingComments, commentText,
      editingCommentId, editingCommentText,
      uploadingFile, taskFiles, newTagName,
      editingField, editFieldValue, saving,
      showEmojiPicker, emojiCategory,
    } = this.state;

    // Guard: localTask may be null on first render before componentDidMount
    if (!localTask) return null;

    const status = STATUS_MAP[localTask.status] || STATUS_MAP['PENDING'];
    const taskTags = localTask.tags || [];

    // ── Inline: title ──
    const renderTitle = () => {
      if (editingField === 'title') {
        return (
          <div style={{ flex: 1 }}>
            <input
              autoFocus
              className="form-control"
              style={{ fontSize: '18px', fontWeight: '700', color: '#1e293b', marginBottom: '8px', height: '44px' }}
              value={editFieldValue}
              onChange={(e) => this.setState({ editFieldValue: e.target.value })}
              onKeyDown={(e) => {
                if (e.key === 'Enter') this.saveInlineField();
                if (e.key === 'Escape') this.cancelEditing();
              }}
            placeholder="أدخل البيانات..."/>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                style={{ background: '#1d9598', color: '#fff', border: 'none', borderRadius: '6px', padding: '5px 16px', cursor: 'pointer', fontSize: '13px' }}
                onClick={this.saveInlineField} disabled={saving}
              >
                {saving ? <Spin size="small" /> : 'حفظ'}
              </button>
              <button
                style={{ background: '#f1f5f9', color: '#64748b', border: 'none', borderRadius: '6px', padding: '5px 12px', cursor: 'pointer', fontSize: '13px' }}
                onClick={this.cancelEditing}
              >
                إلغاء
              </button>
            </div>
          </div>
        );
      }
      return (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', flex: 1 }}>
          <h2 className="task-large-title" style={{ flex: 1, margin: 0 }}>{localTask.title}</h2>
          <MDBIcon
            icon="pen"
            title="تعديل العنوان"
            style={{ color: '#94a3b8', cursor: 'pointer', fontSize: '13px', marginTop: '6px', flexShrink: 0 }}
            onClick={() => this.startEditing('title')}
          />
        </div>
      );
    };

    // ── Inline: description ──
    const renderDescription = () => {
      if (editingField === 'description') {
        return (
          <div>
            <textarea
              autoFocus
              className="form-control"
              style={{ fontSize: '14px', color: '#475569', minHeight: '100px', resize: 'vertical', marginBottom: '8px' }}
              value={editFieldValue}
              onChange={(e) => this.setState({ editFieldValue: e.target.value })}
              onKeyDown={(e) => { if (e.key === 'Escape') this.cancelEditing(); }}
            placeholder="أدخل النص هنا..."/>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                style={{ background: '#1d9598', color: '#fff', border: 'none', borderRadius: '6px', padding: '5px 16px', cursor: 'pointer', fontSize: '13px' }}
                onClick={this.saveInlineField} disabled={saving}
              >
                {saving ? <Spin size="small" /> : 'حفظ'}
              </button>
              <button
                style={{ background: '#f1f5f9', color: '#64748b', border: 'none', borderRadius: '6px', padding: '5px 12px', cursor: 'pointer', fontSize: '13px' }}
                onClick={this.cancelEditing}
              >
                إلغاء
              </button>
            </div>
          </div>
        );
      }
      return (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
          <p className="description-text" style={{ flex: 1, margin: 0, color: localTask.description ? '#475569' : '#94a3b8' }}>
            {localTask.description || 'اضغط ✏️ لإضافة وصف...'}
          </p>
          <MDBIcon
            icon="pen"
            title="تعديل الوصف"
            style={{ color: '#94a3b8', cursor: 'pointer', fontSize: '13px', marginTop: '2px', flexShrink: 0 }}
            onClick={() => this.startEditing('description')}
          />
        </div>
      );
    };

    return (
      <div className="task-drawer-overlay" onClick={this.handleClose}>
        <div className="task-drawer" onClick={(e) => e.stopPropagation()}>

          {/* ── Fixed Header ── */}
          <div className="drawer-fixed-header">
            <div className="drawer-header">
              <button className="drawer-close-btn" onClick={this.handleClose}>
                <MDBIcon icon="times" />
              </button>
              <div className="drawer-header-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <button
                  onClick={this.handleToggleFinish}
                  style={{
                    background: localTask.status === 'FINISHED' ? '#e8f5e9' : '#f8fafc',
                    border: localTask.status === 'FINISHED' ? '1px solid #10b981' : '1px solid #cbd5e1',
                    borderRadius: '20px',
                    padding: '4px 12px',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    color: localTask.status === 'FINISHED' ? '#10b981' : '#475569',
                    fontSize: '13px',
                    fontWeight: '600',
                    transition: 'all 0.2s ease',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                  }}
                  title={localTask.status === 'FINISHED' ? 'المهمة مكتملة (اضغط لإعادتها)' : 'تعليم المهمة كمنتهية'}
                >
                  <MDBIcon icon="check-circle" far={localTask.status !== 'FINISHED'} style={{ color: localTask.status === 'FINISHED' ? '#10b981' : '#1d9598', fontSize: '15px' }} />
                  <span>{localTask.status === 'FINISHED' ? 'تمت بنجاح' : 'إكمال المهمة'}</span>
                </button>
                <span className="task-id">المهمة رقم  #{localTask.id}</span>
                {saving && <Spin size="small" style={{ marginRight: '4px' }} />}
                <button
                  onClick={this.handleDeleteTask}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', fontSize: '14px', marginRight: 'auto', padding: '0 4px' }}
                  title="حذف المهمة"
                >
                  <MDBIcon icon="trash-alt" />
                </button>
              </div>
            </div>

            {/* Title (inline editable) */}
            <div className="task-title-row" style={{ alignItems: 'flex-start' }}>
              {renderTitle()}
            </div>

            {/* Tabs */}
            <div className="drawer-tabs-wrapper">
              <div className="drawer-tabs">
                {[
                  { key: 'details', label: 'التفاصيل' },
                  { key: 'comments', label: `التعليقات (${comments.length})` },
                  { key: 'files', label: `المرفقات (${taskFiles.length})` },
                ].map(tab => (
                  <button
                    key={tab.key}
                    className={`drawer-tab ${activeTab === tab.key ? 'active' : ''}`}
                    onClick={() => onSetActiveTab(tab.key)}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* ── Body ── */}
          <div className="drawer-body">

            {/* ══ Details ══ */}
            {activeTab === 'details' && (
              <div className="drawer-content-single-column">

                {/* Description */}
                <div className="detail-section-card description-card">
                  <h4 className="info-section-title">
                    <MDBIcon far icon="file-alt" style={{ color: '#1D9598', marginLeft: '8px' }} />
                    الوصف التفصيلي
                  </h4>
                  {renderDescription()}
                </div>

                {/* Properties */}
                <div className="detail-section-card properties-grid-card">
                  <h4 className="info-section-title">
                    <MDBIcon icon="info-circle" style={{ color: '#1D9598', marginLeft: '8px' }} />
                    معلومات المهمة
                  </h4>
                  <div className="modern-properties-grid">

                    {/* Status */}
                    <div className="grid-property-item">
                      <span className="grid-prop-label">الحالة</span>
                      <span className="status-badge" style={{ backgroundColor: status.bg, color: status.color, marginTop: '4px', display: 'inline-flex' }}>
                        <span className="status-dot" style={{ backgroundColor: status.color }}></span>
                        {status.label}
                      </span>
                    </div>

                    {/* Created At */}
                    <div className="grid-property-item">
                      <span className="grid-prop-label">تاريخ الإنشاء</span>
                      <span style={{ fontSize: '13px', color: '#475569', marginTop: '4px', display: 'block' }}>
                        {localTask.createdAt ? moment(localTask.createdAt).format('YYYY/MM/DD') : '—'}
                      </span>
                    </div>

                    {/* Deadline — instant local update + saves */}
                    <div className="grid-property-item">
                      <span className="grid-prop-label">تاريخ الإنتهاء</span>
                      <DatePicker
                        format="YYYY-MM-DD"
                        placeholder="تاريخ الإنتهاء"
                        value={localTask.deadline ? moment(localTask.deadline) : null}
                        onChange={(date, dateString) => {
                          // Optimistic local update first
                          this.setState(prev => ({
                            localTask: { ...prev.localTask, deadline: dateString || null },
                          }));
                          this.updateTask({ deadline: dateString || null });
                        }}
                        style={{ width: '100%' }}
                        disabled={saving}
                      />
                    </div>

                    {/* Assignee */}
                    <div className="grid-property-item">
                      <span className="grid-prop-label">المسؤول</span>
                      <TreeSelect
                        virtual={false}
                        value={typeof localTask.assignedTo === 'object' ? (localTask.assignedTo?.id || localTask.assignedTo?._id) : (localTask.assignedTo || undefined)}
                        style={{ width: '100%' }}
                        placeholder="اختر المسؤول"
                        allowClear
                        disabled={saving}
                        onChange={(value) => this.updateTask({ assignedTo: value || '' })}
                      >
                        {(employees || []).map(emp => (
                          <TreeNode key={emp.id || emp._id} value={emp.id || emp._id} title={emp.fullname} />
                        ))}
                      </TreeSelect>
                    </div>

                    {/* Project */}
                    <div className="grid-property-item">
                      <span className="grid-prop-label">المشروع</span>
                      <TreeSelect
                        virtual={false}
                        value={typeof localTask.project === 'object' ? (localTask.project?.id || localTask.project?._id) : (localTask.project || undefined)}
                        style={{ width: '100%' }}
                        placeholder="اختر المشروع"
                        disabled={saving}
                        onChange={(value) => this.updateTask({ project: value })}
                      >
                        {(projects || []).map(p => (
                          <TreeNode key={p.id || p._id} value={p.id || p._id} title={isRTL ? p.name_ar : p.name_en} />
                        ))}
                      </TreeSelect>
                    </div>

                  </div>
                </div>

                {/* Tags */}
                <div className="detail-section-card tags-card">
                  <h4 className="info-section-title">
                    <MDBIcon icon="tags" style={{ color: '#1D9598', marginLeft: '8px' }} />
                    الوسوم والتاجات
                  </h4>
                  <div className="task-tags-wrapper" style={{ marginBottom: '12px' }}>
                    {taskTags.length === 0 && (
                      <span style={{ fontSize: '13px', color: '#94a3b8' }}>لا توجد وسوم</span>
                    )}
                    {taskTags.map((tag, idx) => {
                      const tagStr = typeof tag === 'string' ? tag : (tag.text || '');
                      const c = getTagColor(tagStr);
                      return (
                        <span key={idx} className="premium-tag" style={{ backgroundColor: c.bg, color: c.text }}>
                          {tagStr}
                          <MDBIcon
                            icon="times"
                            className="tag-delete-icon"
                            style={{ cursor: 'pointer', marginRight: '6px' }}
                            onClick={() => {
                              const newTags = taskTags.filter((_, i) => i !== idx);
                              this.updateTask({ tags: newTags });
                            }}
                          />
                        </span>
                      );
                    })}
                  </div>
                  <div className="tag-input-group">
                    <input
                      type="text"
                      placeholder="أدخل تاغ جديد..."
                      className="form-control tag-input"
                      value={newTagName}
                      disabled={saving}
                      onChange={(e) => this.setState({ newTagName: e.target.value })}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && newTagName.trim()) {
                          this.updateTask({ tags: [...taskTags, newTagName.trim()] });
                          this.setState({ newTagName: '' });
                        }
                      }}
                    />
                    <MDBBtn
                      style={{ width: '80px', background: 'linear-gradient(135deg, #1D9598, #127275)', color: '#fff', borderRadius: '6px', padding: '8px 20px', margin: '0', fontSize: '13px' }}
                      disabled={saving}
                      onClick={() => {
                        if (!newTagName.trim()) return;
                        this.updateTask({ tags: [...taskTags, newTagName.trim()] });
                        this.setState({ newTagName: '' });
                      }}
                    >
                      إضافة
                    </MDBBtn>
                  </div>
                </div>

              </div>
            )}

            {/* ══ Comments ══ */}
            {activeTab === 'comments' && (
              <div className="comments-container" style={{ maxWidth: '700px' }}>
                <h4 className="info-section-title">التعليقات</h4>
                <div className="comment-input-box">
                  <div className="mentions-input-container">
                    <div
                      ref={this.mentionsBackdropRef}
                      className="mentions-backdrop"
                      aria-hidden="true"
                    >
                      {this.renderCommentText(commentText)}
                    </div>
                    <Mentions
                      rows={3}
                      placeholder="اكتب تعليقاً... (اكتب @ للإشارة للموظفين)"
                      className="comment-textarea comment-textarea-mentions"
                      value={commentText}
                      onChange={(val) => this.setState({ commentText: val })}
                      onScroll={this.handleMentionsScroll}
                      onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); this.handleAddComment(); } }}
                      direction={isRTL ? 'rtl' : 'ltr'}
                    >
                      {(employees || []).map((emp) => {
                        const name = emp.fullname || emp.name || '';
                        const empId = emp.id || emp._id;
                        if (!name) return null;
                        return (
                          <MentionOption key={empId} value={name} className="mention-option-item">
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              {emp.img ? (
                                <img src={emp.img} alt={name} style={{ width: 22, height: 22, borderRadius: '50%', objectFit: 'cover' }} />
                              ) : (
                                <div style={{ width: 22, height: 22, borderRadius: '50%', background: '#1d9598', color: '#fff', fontSize: 11, fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                  {name[0]}
                                </div>
                              )}
                              <span>{name}</span>
                            </div>
                          </MentionOption>
                        );
                      })}
                    </Mentions>
                  </div>
                  <div className="comment-actions">
                    <div className="comment-toolbar" style={{ position: 'relative' }}>
                      <MDBIcon
                        far
                        icon="smile"
                        style={{ cursor: 'pointer', color: showEmojiPicker ? '#1d9598' : undefined }}
                        onClick={() => this.setState(prev => ({ showEmojiPicker: !prev.showEmojiPicker }))}
                      />
                      {showEmojiPicker && (
                        <div className="emoji-picker-popup" ref={this.emojiPickerRef}>
                          <div className="emoji-picker-tabs">
                            {EMOJI_CATEGORIES.map((cat, idx) => (
                              <button
                                key={cat.name}
                                type="button"
                                className={`emoji-tab${emojiCategory === idx ? ' emoji-tab-active' : ''}`}
                                onClick={() => this.setState({ emojiCategory: idx })}
                              >
                                {cat.name}
                              </button>
                            ))}
                          </div>
                          <div className="emoji-picker-grid">
                            {EMOJI_CATEGORIES[emojiCategory].emojis.map((em) => (
                              <button
                                key={em}
                                type="button"
                                className="emoji-item"
                                onClick={() => this.setState(prev => ({
                                  commentText: prev.commentText + em,
                                  showEmojiPicker: false,
                                }))}
                              >
                                {em}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                      <span
                        title="إشارة لموظف (@)"
                        style={{ cursor: 'pointer', fontWeight: '700', fontSize: '15px', color: '#1d9598', userSelect: 'none' }}
                        onClick={() => this.setState(prev => ({ commentText: prev.commentText + '@' }))}
                      >
                        @
                      </span>
                    </div>
                    <button className="comment-send-btn" onClick={this.handleAddComment}>
                      <MDBIcon icon="paper-plane" style={{ fontSize: '12px' }} />
                    </button>
                  </div>
                </div>

                {loadingComments && <div style={{ textAlign: 'center', padding: '20px' }}><Spin size="small" /></div>}

                {!loadingComments && comments.map((comment) => (
                  <div key={comment.id} className="comment-thread">
                    {comment.user?.img
                      ? <img src={comment.user.img} alt={comment.user?.fullname} className="comment-avatar" />
                      : (
                        <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: '#1d9598', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: '700', fontSize: '14px', flexShrink: 0 }}>
                          {(comment.user?.fullname || 'م')[0]}
                        </div>
                      )
                    }
                    <div className="comment-content-wrapper">
                      <div className="comment-author-row">
                        <span className="comment-author-name">{comment.user?.fullname || 'مجهول'}</span>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          <span className="comment-time">{comment.createdAt ? moment(comment.createdAt).fromNow() : ''}</span>
                          <MDBIcon icon="pen" style={{ fontSize: '11px', color: '#94a3b8', cursor: 'pointer' }}
                            onClick={() => this.setState({ editingCommentId: comment.id, editingCommentText: comment.comment })} />
                          <MDBIcon icon="trash-alt" style={{ fontSize: '11px', color: '#ef4444', cursor: 'pointer' }}
                            onClick={() => this.handleDeleteComment(comment.id)} />
                        </div>
                      </div>
                      {editingCommentId === comment.id ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '6px' }}>
                          <div className="mentions-input-container mentions-input-container-edit">
                            <div
                              ref={this.editMentionsBackdropRef}
                              className="mentions-backdrop"
                              aria-hidden="true"
                            >
                              {this.renderCommentText(editingCommentText)}
                            </div>
                            <Mentions
                              rows={2}
                              className="comment-textarea-edit comment-textarea-mentions"
                              style={{ fontSize: '13px' }}
                              value={editingCommentText}
                              onChange={(val) => this.setState({ editingCommentText: val })}
                              onScroll={this.handleEditMentionsScroll}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                  e.preventDefault();
                                  this.handleUpdateComment(comment.id);
                                }
                              }}
                              direction={isRTL ? 'rtl' : 'ltr'}
                            >
                              {(employees || []).map((emp) => {
                                const name = emp.fullname || emp.name || '';
                                const empId = emp.id || emp._id;
                                if (!name) return null;
                                return (
                                  <MentionOption key={empId} value={name} className="mention-option-item">
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                      {emp.img ? (
                                        <img src={emp.img} alt={name} style={{ width: 20, height: 20, borderRadius: '50%', objectFit: 'cover' }} />
                                      ) : (
                                        <div style={{ width: 20, height: 20, borderRadius: '50%', background: '#1d9598', color: '#fff', fontSize: 10, fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                          {name[0]}
                                        </div>
                                      )}
                                      <span>{name}</span>
                                    </div>
                                  </MentionOption>
                                );
                              })}
                            </Mentions>
                          </div>
                          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                            <button style={{ background: '#1d9598', color: '#fff', border: 'none', borderRadius: '6px', padding: '4px 12px', cursor: 'pointer', fontSize: '12px', whiteSpace: 'nowrap' }}
                              onClick={() => this.handleUpdateComment(comment.id)}>حفظ</button>
                            <button style={{ background: '#f1f5f9', color: '#64748b', border: 'none', borderRadius: '6px', padding: '4px 10px', cursor: 'pointer', fontSize: '12px' }}
                              onClick={() => this.setState({ editingCommentId: null })}>إلغاء</button>
                          </div>
                        </div>
                      ) : (
                        <p className="comment-text">{this.renderCommentText(comment.comment)}</p>
                      )}
                    </div>
                  </div>
                ))}

                {!loadingComments && comments.length === 0 && (
                  <div style={{ textAlign: 'center', padding: '30px', color: '#94a3b8' }}>
                    <MDBIcon far icon="comments" style={{ fontSize: '32px', marginBottom: '10px', display: 'block' }} />
                    لا توجد تعليقات بعد
                  </div>
                )}
              </div>
            )}

            {/* ══ Files ══ */}
            {activeTab === 'files' && (
              <div style={{ maxWidth: '600px' }}>
                <h4 className="info-section-title">
                  <MDBIcon icon="paperclip" style={{ marginLeft: '8px' }} />
                  الملفات المرفقة ({taskFiles.length})
                </h4>
                <div className="attachment-list">
                  {taskFiles.length === 0 && (
                    <div style={{ textAlign: 'center', padding: '30px', color: '#94a3b8', border: '1px dashed #e2e8f0', borderRadius: '8px' }}>
                      <MDBIcon far icon="folder-open" style={{ fontSize: '28px', marginBottom: '8px', display: 'block' }} />
                      لا توجد مرفقات
                    </div>
                  )}
                  {taskFiles.map((fileUrl, idx) => {
                    const name = getFileName(fileUrl);
                    return (
                      <div key={idx} className="attachment-item">
                        <div className="attachment-info">
                          <MDBIcon far icon={getFileIcon(fileUrl)} className="file-icon" />
                          <div>
                            <span className="file-name" style={{ display: 'block' }}>{name}</span>
                            <a href={fileUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: '11px', color: '#1d9598' }}>عرض الملف</a>
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          <a href={fileUrl} download={name}>
                            <MDBIcon icon="download" style={{ color: '#64748b', cursor: 'pointer', fontSize: '14px' }} />
                          </a>
                          <MDBIcon icon="trash-alt" style={{ color: '#ef4444', cursor: 'pointer', fontSize: '14px' }}
                            onClick={() => this.handleRemoveFile(fileUrl)} />
                        </div>
                      </div>
                    );
                  })}
                </div>
                <input type="file" ref={this.fileInputRef} style={{ display: 'none' }} onChange={this.handleAddFile} />
                <button
                  className="upload-file-btn"
                  style={{ marginTop: '15px' }}
                  disabled={uploadingFile}
                  onClick={() => this.fileInputRef.current.click()}
                >
                  {uploadingFile
                    ? <><Spin size="small" /><span style={{ marginRight: '8px' }}>جاري الرفع...</span></>
                    : <><MDBIcon icon="upload" /><span>رفع ملف جديد</span></>
                  }
                </button>
              </div>
            )}

          </div>
        </div>
      </div>
    );
  }
}

export default TaskInfo;
